import * as React from 'react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type OnChangeFn,
  type PaginationState,
  type Row,
  useReactTable,
} from '@tanstack/react-table';

import { NumberedPagination } from '@kioskgaming/ui';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { cn } from '@/lib/utils';

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Server-driven pagination */
  manualPagination?: boolean;
  pageCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  /** Stable row id (required if `TData` has no `id` field). */
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  emptyMessage?: string;
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  manualPagination,
  pageCount,
  pagination,
  onPaginationChange,
  getRowId,
  emptyMessage = 'No results.',
  className,
}: DataTableProps<TData, TValue>) {
  const manual =
    Boolean(manualPagination) && pagination != null && onPaginationChange != null;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(manual
      ? {
          manualPagination: true as const,
          pageCount: pageCount ?? 0,
          onPaginationChange,
          state: { pagination: pagination as PaginationState },
        }
      : {}),
    getRowId:
      getRowId ??
      ((row, i) => {
        const r = row as { id?: string };
        return r.id != null && r.id !== '' ? String(r.id) : `row-${i}`;
      }),
  });

  const showPagination =
    manual && typeof pageCount === 'number' && pageCount > 1 && pagination != null;

  return (
    <div className={cn('w-full', className)}>
      <div className="overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination ? (
        <div className="flex flex-col gap-3 border-t border-border px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.pageIndex + 1} / {pageCount}
          </p>
          <NumberedPagination
            currentPage={pagination.pageIndex + 1}
            totalPages={pageCount}
            onPageChange={(p) => table.setPageIndex(p - 1)}
            variant="admin"
          />
        </div>
      ) : null}
    </div>
  );
}
